<app-modal
  [isVisible]="isVisible()"
  [config]="modalConfig()"
  [warnBeforeClose]="hasUnsavedWork()"
  (confirmed)="onContinue()"
  (cancelled)="onDismiss()"
  (closed)="onDismiss()"
  (secondary)="onBack()"
>
  @if (isLoading()) {
    <app-modal-status
      variant="loading"
      [title]="'modals.create_task.creating_status' | transloco"
      [description]="'modals.create_task.creating_description' | transloco"
    >
      <app-huxley [state]="'thinking'" [size]="72" />
    </app-modal-status>
  } @else if (successMessage()) {
    <app-modal-status variant="success" [title]="successMessage()!">
      <app-huxley [state]="'success'" [size]="72" />
    </app-modal-status>
  } @else {
    <app-step-indicator
      [steps]="steps()"
      [currentStep]="currentStep()"
      [maxReachedStep]="maxReachedStep()"
      [clickable]="true"
      (stepClicked)="onStepClicked($event)"
    ></app-step-indicator>

    <!-- ── STEP: LANGUAGE ──
         Locks the content language before the Details step so task
         templates load in the right variant and any AI-generated
         copy honours the choice. Extracted from an inline picker
         that used to sit inside the Details form. -->
    @if (currentStep() === 'language') {
      <div class="step-body">
        <p class="step-hint">{{ 'language_step.hint_task' | transloco }}</p>
        <app-language-picker [(language)]="contentLanguage" appearance="cards" />
      </div>
    }

    <!-- ── STEP: DETAILS ── -->
    @if (currentStep() === 'details') {
      <div class="step-body" [formGroup]="form">
        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        @if (preset() && isCreateTask()) {
          <div class="field">
            <span class="field-label" id="create-what-label"
              >{{ 'modals.create_task.create_what' | transloco }}
              <span class="req" aria-hidden="true">*</span></span
            >
            <div class="link-grid" role="radiogroup" aria-labelledby="create-what-label">
              @for (option of createCategoryOptions(); track option.value) {
                <button
                  type="button"
                  role="radio"
                  class="link-card"
                  [class.active]="presetCreateCategory() === option.value"
                  [attr.aria-checked]="presetCreateCategory() === option.value"
                  (click)="selectCreateCategory(option.value)"
                >
                  <span class="link-icon" aria-hidden="true">
                    @if (option.sectionIcon; as key) {
                      <app-section-icon [name]="key" />
                    }
                  </span>
                  <span class="link-label">{{ option.label }}</span>
                </button>
              }
            </div>
          </div>
          @if (presetCreateCategory()) {
            <ng-container [ngTemplateOutlet]="createChoice" />
          }
        }

        <div class="field">
          <label for="task-name"
            >{{ 'modals.create_task.field_name' | transloco }}
            <span class="req" aria-hidden="true">*</span></label
          >
          <input
            id="task-name"
            type="text"
            class="input"
            formControlName="name"
            [placeholder]="'modals.create_task.name_placeholder' | transloco"
            required
          />
        </div>

        <div class="field">
          <label for="task-desc"
            >{{ 'modals.create_task.description_label' | transloco }}
            <span class="optional">{{
              'modals.create_task.description_optional' | transloco
            }}</span></label
          >
          <textarea
            id="task-desc"
            class="input textarea"
            formControlName="description"
            [placeholder]="'modals.create_task.description_placeholder' | transloco"
            rows="4"
          ></textarea>
        </div>

        <div class="toggle-card">
          <div class="toggle-text">
            <p class="toggle-title">{{ 'modals.create_task.requires_approval' | transloco }}</p>
            <p class="toggle-desc">
              @if (isCreateTask()) {
                {{ 'modals.create_task.approval_required_helper' | transloco }}
              } @else {
                {{ 'modals.create_task.approval_default_helper' | transloco }}
              }
            </p>
          </div>
          <mat-slide-toggle
            formControlName="needs_approval"
            [disabled]="isCreateTask()"
            [attr.aria-label]="'modals.create_task.requires_approval_aria' | transloco"
          />
        </div>

        <div class="toggle-card">
          <div class="toggle-text">
            <p class="toggle-title">{{ 'modals.create_task.recurring_task' | transloco }}</p>
            <p class="toggle-desc">
              @if (isCreateTask()) {
                {{ 'modals.create_task.recurring_create_helper' | transloco }}
              } @else if (isLinkedControlRecurrent()) {
                {{ 'modals.create_task.recurring_follows_cycle_helper' | transloco }}
              } @else {
                {{ 'modals.create_task.recurring_unavailable_helper' | transloco }}
              }
            </p>
          </div>
          <mat-slide-toggle
            formControlName="recurring"
            [disabled]="!isLinkedControlRecurrent() || isCreateTask()"
            [attr.aria-label]="'modals.create_task.recurring_task_aria' | transloco"
          />
        </div>

        @if (!(isLinkedControlRecurrent() && recurringValue())) {
          <div class="field">
            <label for="task-start"
              >{{ 'modals.create_task.start_date_label' | transloco }}
              <span class="optional">{{
                'modals.create_task.due_date_optional' | transloco
              }}</span></label
            >
            <input id="task-start" type="date" class="input" formControlName="start_date" />
          </div>
          <div class="field">
            <label for="task-due"
              >{{ 'modals.create_task.due_date_label' | transloco }}
              <span class="optional">{{
                'modals.create_task.due_date_optional' | transloco
              }}</span></label
            >
            <input id="task-due" type="date" class="input" formControlName="due_date" />
          </div>
        }

        <!-- Language now lives on a dedicated step earlier in the
             wizard; this read-only pill confirms the pick without
             re-offering it. -->
        <div class="field">
          <span class="field-label">{{ 'language_picker.label' | transloco }}</span>
          <app-language-badge [language]="contentLanguage()" appearance="pill" />
        </div>

        <div class="field">
          <span class="field-label">{{ 'modals.create_task.field_assignees' | transloco }}</span>
          <app-assignee-picker
            [assigneeIds]="selectedAssignees()"
            [members]="orgMembers()"
            [readonly]="false"
            (assigneesChange)="selectedAssignees.set($event)"
          />
        </div>
      </div>
    }

    <!-- ── STEP 2: LINK ── -->
    @if (currentStep() === 'link') {
      <div class="step-body">
        <div class="field">
          <span class="field-label" id="task-type-label">{{
            'modals.create_task.task_type' | transloco
          }}</span>
          <div class="pill-row" role="radiogroup" aria-labelledby="task-type-label">
            @for (type of taskTypes(); track type.value) {
              <button
                type="button"
                role="radio"
                class="type-pill"
                [class.active]="selectedTaskType() === type.value"
                [attr.aria-checked]="selectedTaskType() === type.value"
                (click)="selectTaskType(type.value)"
              >
                {{ type.label }}
              </button>
            }
          </div>
        </div>

        @if (preset(); as p) {
          <div class="field">
            <span class="field-label">{{ 'modals.create_task.link_to' | transloco }}</span>
            <div class="link-locked">
              <span class="link-icon" aria-hidden="true">
                <app-section-icon name="assessment" />
              </span>
              <span class="link-label">{{ p.linkLabel }}</span>
              <mat-icon class="link-locked__icon" aria-hidden="true">lock</mat-icon>
            </div>
            <p class="step-hint">{{ 'modals.create_task.link_locked_hint' | transloco }}</p>
          </div>
        } @else {
          <div class="field">
            <span class="field-label" id="link-to-label">
              {{ 'modals.create_task.link_to' | transloco }}
              @if (!requiresLink()) {
                <span class="optional">{{ 'modals.create_task.optional' | transloco }}</span>
              }
            </span>
            <p class="step-hint">
              @if (requiresLink()) {
                {{
                  'modals.create_task.requires_link_notice'
                    | transloco: { type: taskTypeLabel(selectedTaskType()) }
                }}
              } @else {
                {{ 'modals.create_task.link_optional_hint' | transloco }}
              }
            </p>

            <div class="link-grid" role="radiogroup" aria-labelledby="link-to-label">
              @for (option of linkOptionViews(); track option.value) {
                <button
                  type="button"
                  role="radio"
                  class="link-card"
                  [class.active]="selectedLink() === option.value"
                  [attr.aria-checked]="selectedLink() === option.value"
                  [disabled]="option.disabled"
                  [attr.aria-disabled]="option.disabled"
                  [attr.title]="option.disabledReason"
                  (click)="selectLink(option.value)"
                >
                  <span class="link-icon" aria-hidden="true">
                    @if (option.sectionIcon; as key) {
                      <app-section-icon [name]="key" />
                    } @else {
                      <mat-icon>link_off</mat-icon>
                    }
                  </span>
                  <span class="link-label">{{ option.label }}</span>
                </button>
              }
            </div>
          </div>

          @if (selectedLink() && selectedLink() !== 'none') {
            @if (isCreateTask()) {
              <ng-container [ngTemplateOutlet]="createChoice" />
            } @else {
              <app-toolbar-search
                class="entity-search"
                [placeholder]="
                  'modals.create_task.entity_search_placeholder'
                    | transloco: { kind: selectedLink() }
                "
                [ariaLabel]="
                  'modals.create_task.entity_search_aria' | transloco: { kind: selectedLink() }
                "
                [maxWidth]="null"
                [value]="entitySearchQuery()"
                (valueChange)="onEntitySearchValue($event)"
              />

              <div class="entity-list">
                @if (isEntityLoading()) {
                  <div class="entity-loading">
                    <mat-icon [appSpin]="true" aria-hidden="true">sync</mat-icon>
                    <span>{{ 'modals.create_task.loading' | transloco }}</span>
                  </div>
                } @else if (filteredEntityItems().length === 0) {
                  <p class="entity-empty">{{ 'modals.create_task.no_results' | transloco }}</p>
                } @else {
                  @for (item of filteredEntityItems(); track item.id) {
                    <button
                      type="button"
                      class="entity-item"
                      [class.selected]="selectedEntityId() === item.id"
                      (click)="selectEntity(item.id)"
                    >
                      <app-entity-icon [entity]="item.iconInput" />
                      <span class="entity-info">
                        <span class="entity-name">{{ item.name }}</span>
                        @if (item.subtitle) {
                          <span class="entity-subtitle">{{ item.subtitle }}</span>
                        }
                      </span>
                      <span
                        class="entity-radio"
                        [class.checked]="selectedEntityId() === item.id"
                        aria-hidden="true"
                      ></span>
                    </button>
                  }
                }
              </div>
            }
          }
        }
      </div>
    }

    <!-- ── STEP 3: REVIEW ── -->
    @if (currentStep() === 'review') {
      <div class="step-body">
        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        <p class="step-hint">{{ 'modals.create_task.review_intro' | transloco }}</p>

        <div class="review-table">
          <div class="review-row">
            <span class="review-label">{{
              'modals.create_task.review_task_name' | transloco
            }}</span>
            <span class="review-value">{{ form.controls.name.value || '—' }}</span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.create_task.review_task_type' | transloco
            }}</span>
            <span class="review-value">{{ taskTypeLabel(selectedTaskType()) }}</span>
          </div>
          @if (!recurringValue()) {
            <div class="review-row">
              <span class="review-label">{{
                'modals.create_task.start_date_label' | transloco
              }}</span>
              <span class="review-value">{{
                form.controls.start_date.value ||
                  ('modals.create_task.review_value_no_due_date' | transloco)
              }}</span>
            </div>
          }
          <div class="review-row">
            <span class="review-label">{{ 'modals.create_task.review_due_date' | transloco }}</span>
            <span class="review-value">{{
              form.controls.due_date.value ||
                ('modals.create_task.review_value_no_due_date' | transloco)
            }}</span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.create_task.review_linked_to' | transloco
            }}</span>
            <span class="review-value">
              @if (selectedEntityName()) {
                {{ selectedEntityName() }}
              } @else {
                {{ linkLabel(selectedLink()) }}
              }
            </span>
          </div>
          @if (preset() && isCreateTask()) {
            <div class="review-row">
              <span class="review-label">{{
                'modals.create_task.review_creates' | transloco
              }}</span>
              <span class="review-value">{{ createChoiceLabel() || '—' }}</span>
            </div>
          }
          <div class="review-row">
            <span class="review-label">{{ 'language_picker.label' | transloco }}</span>
            <span class="review-value">
              <app-language-badge [language]="contentLanguage()" appearance="pill" />
            </span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.create_task.review_recurring' | transloco
            }}</span>
            <span class="review-value">{{
              form.controls.recurring.value
                ? ('modals.create_task.review_value_yes' | transloco)
                : ('modals.create_task.review_value_no' | transloco)
            }}</span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.create_task.review_manager_approval' | transloco
            }}</span>
            <span class="review-value">
              {{
                form.controls.needs_approval.value
                  ? ('modals.create_task.review_value_required' | transloco)
                  : ('modals.create_task.review_value_not_required' | transloco)
              }}
            </span>
          </div>
        </div>
      </div>
    }
  }

  <!-- What a Create task creates, once its category is picked: the Link
       step's pick, or the Details step's in the preset flow. -->
  <ng-template #createChoice>
    @if (createKind() === 'document' || createKind() === 'framework') {
      <div class="entity-list">
        @for (opt of createEntitySubtypeOptions(); track opt.value) {
          <button
            type="button"
            class="entity-item"
            [class.selected]="selectedEntitySubtype() === opt.value"
            (click)="selectEntitySubtype(opt.value)"
          >
            @if (createKind() === 'document') {
              <app-entity-icon
                [entity]="{
                  kind: 'document',
                  docKind: getDocKindFromSubtypeCode(opt.value),
                }"
              />
            } @else if (createKind() === 'framework') {
              <app-entity-icon [entity]="{ kind: 'framework', frameworkType: $any(opt.value) }" />
            }
            <span class="entity-info">
              <span class="entity-name">{{ opt.label }}</span>
              <span class="entity-subtitle">{{ opt.description }}</span>
            </span>
            <span
              class="entity-radio"
              [class.checked]="selectedEntitySubtype() === opt.value"
              aria-hidden="true"
            ></span>
          </button>
        }
      </div>
    } @else if (createKind() === 'assessment') {
      <p class="step-hint">
        {{ 'modals.create_task.creates_ai_assessment_info' | transloco }}
      </p>
    } @else {
      <p class="step-hint">
        @if (createKind() === 'control') {
          {{ 'modals.create_task.create_entity_helper_control' | transloco }}
        } @else {
          {{ 'modals.create_task.create_entity_helper_vendor' | transloco }}
        }
      </p>
    }
  </ng-template>
</app-modal>
